import { useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { AuroraAssetGroup, AuroraAwhpData } from '../types'
import { useAuroraStack } from '../hooks'
import { setAuroraTargetTemperature } from '../services/aurora'
import { createErrorNotification, createSuccessNotification } from '../redux/slices/notifications'
import AuroraTargetTemperatureControl from './AuroraTargetTemperatureControl'

interface AuroraAssetGroupTemperatureControlProps {
  auroraAssetGroup: AuroraAssetGroup
  auroraAwhpData: Record<string, AuroraAwhpData>
  beforeSubmit?: (newTargetTemperature: number) => void
  afterSubmit?: (newTargetTemperature: number) => void
  onError?: (error: unknown) => void
}

export default function AuroraAssetGroupTargetTemperatureControl({
  auroraAssetGroup,
  auroraAwhpData,
  beforeSubmit,
  afterSubmit,
  onError
}: AuroraAssetGroupTemperatureControlProps) {
  const { t } = useTranslation('aurora')

  const dispatch = useDispatch()

  const {
    auroras,
    master,
    targetTemperature: initialTargetTemperature,
    minTemperatureLimit,
    handleAuroraAwhpActionError
  } = useAuroraStack(auroraAssetGroup)

  const [targetTemperature, setTargetTemperature] = useState<number | null>(
    initialTargetTemperature
  )

  const masterAwhpData = useMemo(() => {
    if (master === null) {
      return null
    }

    return auroraAwhpData[master.id] ?? null
  }, [auroraAwhpData, master])

  const secondarySupplyTemperature = useMemo(() => {
    if (masterAwhpData !== null) {
      return masterAwhpData.temperature_secondary_supply
    }

    const values = Object.values(auroraAwhpData).map(
      (reading) => reading.temperature_secondary_supply
    )

    if (values.length === 0) {
      return null
    }

    return Math.max(...values)
  }, [auroraAwhpData, masterAwhpData])

  const [isSubmitting, setSubmitting] = useState<boolean>(false)

  async function handleSubmit(value: number) {
    try {
      setSubmitting(true)

      beforeSubmit?.(value)

      await Promise.all(auroras.map((aurora) => setAuroraTargetTemperature(aurora.id, value)))
      dispatch(createSuccessNotification(t('setHeatPumpTargetTemperatureSuccess')))
      setTargetTemperature(value)

      afterSubmit?.(value)
    } catch (error) {
      onError?.(error)
      handleAuroraAwhpActionError(error, t('setHeatPumpTargetTemperatureError'))
    } finally {
      setSubmitting(false)
    }
  }

  if (auroras.length === 0) {
    return null
  }

  return (
    <AuroraTargetTemperatureControl
      value={targetTemperature}
      secondarySupplyTemperature={secondarySupplyTemperature}
      minTemperatureLimit={minTemperatureLimit}
      loading={isSubmitting}
      onSubmit={handleSubmit}
    />
  )
}
